import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';

// Async thunk for getting all models
export const getModels = createAsyncThunk(
  'maestro/getModels',
  async ({ t, lang = 'fr' }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `maestro/models`,
        body: {
          lang: lang?.slice(0, 2) || 'fr',
        },
      });
      return response;
    } catch (error) {
      console.error('Error fetching models:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for getting a single model
export const getSingleModel = createAsyncThunk(
  'maestro/getSingleModel',
  async ({ modelId, t, lang = 'fr' }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `maestro/models/${modelId}`,
        body: {
          lang: lang?.slice(0, 2) || 'fr',
        },
      });
      return response;
    } catch (error) {
      console.error('Error fetching single model:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for getting accounts
export const getAccounts = createAsyncThunk(
  'maestro/getAccounts',
  async ({ t, lang = 'fr' }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `maestro/accounts`,
        body: {
          lang: lang?.slice(0, 2) || 'fr',
        },
      });
      return response;
    } catch (error) {
      console.error('Error fetching accounts:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk for getting orders
export const getOrders = createAsyncThunk(
  'maestro/getOrders',
  async ({ t, lang = 'fr' }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        t,
        method: 'GET',
        url: `maestro/orders`,
        body: {
          lang: lang?.slice(0, 2) || 'fr',
        },
      });
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  // Models state
  models: [],
  modelsLoading: false,
  modelsError: null,

  // Single model state
  currentModel: {
    id: null,
    name: null,
    description: null,
    createdAt: null,
    updatedAt: null,
    structures: [],
    models: [],
  },
  currentModelLoading: false,
  currentModelError: null,

  // Accounts state
  accounts: [],
  accountsLoading: false,
  accountsError: null,

  // Orders state
  orders: [],
  ordersLoading: false,
  ordersError: null,

  // General state
  lastUpdated: null,
};

// Slice definition
const maestroSlice = createSlice({
  name: 'maestro',
  initialState,
  reducers: {
    // Clear all errors
    clearErrors: (state) => {
      state.modelsError = null;
      state.currentModelError = null;
      state.accountsError = null;
      state.ordersError = null;
    },

    // Clear current model
    clearCurrentModel: (state) => {
      state.currentModel = null;
      state.currentModelError = null;
    },

    // Set models manually (useful for local updates)
    setModels: (state, action) => {
      state.models = action.payload;
      state.lastUpdated = new Date().toISOString();
    },

    // Set accounts manually
    setAccounts: (state, action) => {
      state.accounts = action.payload;
      state.lastUpdated = new Date().toISOString();
    },

    // Set orders manually
    setOrders: (state, action) => {
      state.orders = action.payload;
      state.lastUpdated = new Date().toISOString();
    },

    // Update a single model in the models array
    updateModel: (state, action) => {
      const { modelId, updatedData } = action.payload;
      const modelIndex = state.models.findIndex(
        (model) => model.id === modelId
      );
      if (modelIndex !== -1) {
        state.models[modelIndex] = {
          ...state.models[modelIndex],
          ...updatedData,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Add a new model to the models array
    addModel: (state, action) => {
      state.models.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    // Remove a model from the models array
    removeModel: (state, action) => {
      const modelId = action.payload;
      state.models = state.models.filter((model) => model.id !== modelId);
      state.lastUpdated = new Date().toISOString();
    },

    // Add a new account
    addAccount: (state, action) => {
      state.accounts.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    // Update an account
    updateAccount: (state, action) => {
      const { accountId, updatedData } = action.payload;
      const accountIndex = state.accounts.findIndex(
        (account) => account.id === accountId
      );
      if (accountIndex !== -1) {
        state.accounts[accountIndex] = {
          ...state.accounts[accountIndex],
          ...updatedData,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },

    // Remove an account
    removeAccount: (state, action) => {
      const accountId = action.payload;
      state.accounts = state.accounts.filter(
        (account) => account.id !== accountId
      );
      state.lastUpdated = new Date().toISOString();
    },

    // Add a new order
    addOrder: (state, action) => {
      state.orders.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    // Update an order
    updateOrder: (state, action) => {
      const { orderId, updatedData } = action.payload;
      const orderIndex = state.orders.findIndex(
        (order) => order.id === orderId
      );
      if (orderIndex !== -1) {
        state.orders[orderIndex] = {
          ...state.orders[orderIndex],
          ...updatedData,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getModels cases
      .addCase(getModels.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(getModels.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.models = action.payload?.models || action.payload || [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getModels.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.payload || action.error.message;
      })

      // getSingleModel cases
      .addCase(getSingleModel.pending, (state) => {
        state.currentModelLoading = true;
        state.currentModelError = null;
      })
      .addCase(getSingleModel.fulfilled, (state, action) => {
        state.currentModelLoading = false;
        state.currentModel = action.payload?.model || action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getSingleModel.rejected, (state, action) => {
        state.currentModelLoading = false;
        state.currentModelError = action.payload || action.error.message;
      })

      // getAccounts cases
      .addCase(getAccounts.pending, (state) => {
        state.accountsLoading = true;
        state.accountsError = null;
      })
      .addCase(getAccounts.fulfilled, (state, action) => {
        state.accountsLoading = false;
        state.accounts = action.payload?.accounts || action.payload || [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getAccounts.rejected, (state, action) => {
        state.accountsLoading = false;
        state.accountsError = action.payload || action.error.message;
      })

      // getOrders cases
      .addCase(getOrders.pending, (state) => {
        state.ordersLoading = true;
        state.ordersError = null;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.ordersLoading = false;
        state.orders = action.payload?.orders || action.payload || [];
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.ordersLoading = false;
        state.ordersError = action.payload || action.error.message;
      });
  },
});

// Export actions
export const {
  clearErrors,
  clearCurrentModel,
  setModels,
  setAccounts,
  setOrders,
  updateModel,
  addModel,
  removeModel,
  addAccount,
  updateAccount,
  removeAccount,
  addOrder,
  updateOrder,
} = maestroSlice.actions;

// Selectors
export const selectModels = (state) => state.maestro.models;
export const selectModelsLoading = (state) => state.maestro.modelsLoading;
export const selectModelsError = (state) => state.maestro.modelsError;

export const selectCurrentModel = (state) => state.maestro.currentModel;
export const selectCurrentModelLoading = (state) =>
  state.maestro.currentModelLoading;
export const selectCurrentModelError = (state) =>
  state.maestro.currentModelError;

export const selectAccounts = (state) => state.maestro.accounts;
export const selectAccountsLoading = (state) => state.maestro.accountsLoading;
export const selectAccountsError = (state) => state.maestro.accountsError;

export const selectOrders = (state) => state.maestro.orders;
export const selectOrdersLoading = (state) => state.maestro.ordersLoading;
export const selectOrdersError = (state) => state.maestro.ordersError;

export const selectLastUpdated = (state) => state.maestro.lastUpdated;

// Compound selectors
export const selectMaestroLoading = (state) =>
  state.maestro.modelsLoading ||
  state.maestro.currentModelLoading ||
  state.maestro.accountsLoading ||
  state.maestro.ordersLoading;

export const selectMaestroErrors = (state) => ({
  models: state.maestro.modelsError,
  currentModel: state.maestro.currentModelError,
  accounts: state.maestro.accountsError,
  orders: state.maestro.ordersError,
});

export const selectHasAnyError = (state) =>
  state.maestro.modelsError ||
  state.maestro.currentModelError ||
  state.maestro.accountsError ||
  state.maestro.ordersError;

export default maestroSlice.reducer;
